import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LatLng, LatLngBounds, LatLngBoundsLiteral, Map as LeafletMap } from 'leaflet';
import { MapContainer, TileLayer } from 'react-leaflet';

import { Spinner, useDisclosure } from '@ds';
import { useMapFilter } from '@lp/features/RoutePlanner/hooks/useMapFilter';
import { getClosestHubByDistance, getDonorHospitalMarker, getTxCenterMarker } from '@lp/features/RoutePlanner/utils';
import { HighlightedRouteType, HospitalMarkerType, TxCenterMarkerType } from '@lp/features/RoutePlanner/types';
import { SuggestedRoutes } from '@lp/features/RoutePlanner/components/SuggestedRoutes';
import { useRoutePlannerData } from '@lp/features/RoutePlanner/contexts/RoutePlannerDataContext';
import { useAuthContext } from '@lp/core/auth';
import { HERE_MAPS_TILES_URL } from '@/constants/env.constants';
import { AircraftLegResource, BaseLegResourceType } from '@/api/apis/route';
import { CaseResourceType, CaseStatus } from '@/api/apis/case';
import { RouteBuilderPermissions } from '@/permissions';
import { isAllowedStatus } from '@/utils/helpers';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { RouteMap } from './RouteMap';
import { MapFilters } from './MapFilters';
import { MapControls } from './MapControls';
import { MapMarkers } from './MapMarkers';

import 'leaflet/dist/leaflet.css';
import '@lp/features/RoutePlanner/leaflet.css';

export const RoutePlannerMap = () => {
  const { checkPermission } = useAuthContext();
  const {
    organType,
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
    donorHospitalTime,
    donorHospitalDate,
    donorHospitalTimeZone,
    status,
  } = useBaseCaseDetails();
  const { locations: list, caseLocations, mainRoute: route, resources } = useRoutePlannerData();
  const { Aircraft: aircraftsList } = resources;
  const { isOpen: isSuggestedRoutesOpen, onOpen, onClose } = useDisclosure({ defaultOpen: false });
  const [highlightedRoute, setHighlightedRoute] = useState<HighlightedRouteType | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const [showRoute, setShowRoute] = useState(false);
  const showRouteInited = useRef(false);
  const { isLoading, markers, aircraftsMarkers, filters, updateFilters } = useMapFilter();

  useEffect(() => {
    if (!showRouteInited.current && route.id) {
      setShowRoute(!!route.id && route.legs.length > 0);
      showRouteInited.current = true;
    }
  }, [route]);

  const handleShowRoute = useCallback((showRouteState: boolean) => {
    setShowRoute(showRouteState);
  }, []);

  const donorHospitalMarker = useMemo<HospitalMarkerType>(
    () => getDonorHospitalMarker(donorHospital!),
    [donorHospital],
  );
  const txCenterMarker = useMemo<TxCenterMarkerType | null>(
    () => (txCenter ? getTxCenterMarker(txCenter) : null),
    [txCenter],
  );

  const markersList = useMemo(
    () => [donorHospitalMarker, ...(txCenterMarker ? [txCenterMarker] : []), ...markers],
    [markers, donorHospitalMarker, txCenterMarker],
  );

  const closestHub = useMemo(() => {
    const hubs = markers.filter((m) => m.type === 'hub');

    return getClosestHubByDistance(hubs, donorHospitalMarker);
  }, [markers, donorHospitalMarker]);

  const latLngBounds = useMemo(() => {
    const hubLocation = closestHub.hub && (closestHub.hub?.location as LatLng);

    if (hubLocation) {
      return new LatLngBounds([
        [donorHospital!.location.latitude, donorHospital!.location.longitude],
        ...(txCenter ? [[txCenter.location.latitude, txCenter.location.longitude]] : []),
        [hubLocation.lat, hubLocation.lng],
      ] as LatLngBoundsLiteral).pad(0.1);
    }

    return new LatLngBounds([
      [donorHospital!.location.latitude, donorHospital!.location.longitude],
      ...(txCenter ? [[txCenter.location.latitude, txCenter.location.longitude]] : []),
    ] as LatLngBoundsLiteral).pad(0.1);
  }, [donorHospital, txCenter, closestHub]);

  const hasOrTime = !!donorHospitalDate && !!donorHospitalTime && !!donorHospitalTimeZone;

  const { legs, aircrafts, locations } = useMemo(() => {
    const legList = highlightedRoute?.legs || route.legs;
    const aircraftResourcesList =
      highlightedRoute?.resources[CaseResourceType.Aircraft] ||
      aircraftsList.map<BaseLegResourceType<AircraftLegResource>>((a) => ({
        id: a.resourceId,
        type: CaseResourceType.Aircraft,
        data: a.data,
      })) ||
      [];

    const locationsList = highlightedRoute?.locations ? [...caseLocations, ...highlightedRoute.locations] : list;

    return { legs: legList, aircrafts: aircraftResourcesList, locations: locationsList };
  }, [route, highlightedRoute, aircraftsList, list, caseLocations]);

  const handleSuggestionsClose = () => {
    setShowRoute(false);
    onClose();
    setHighlightedRoute(null);
  };

  const handleHighlightRoute = useCallback((routePlan: HighlightedRouteType | null) => {
    setShowRoute(!!routePlan);
    setHighlightedRoute(routePlan);
  }, []);

  const canViewSuggestedRoute = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AlgorithmView);
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isActiveCaseStatus;
  }, [status, checkPermission]);

  return (
    <>
      {isLoading && (
        <div
          className="absolute z-403 w-full h-full top-0 left-0 flex justify-center items-center"
          style={{ backgroundColor: 'rgba(246, 246, 251, 0.5)' }}
        >
          <Spinner size="md" />
        </div>
      )}
      {!isSuggestedRoutesOpen && filters && (
        <MapFilters
          filters={filters}
          updateFilters={updateFilters}
          showRoute={showRoute}
          handleShowRoute={handleShowRoute}
          timezone={donorHospitalTimeZone!}
        />
      )}
      <MapContainer
        center={donorHospitalMarker.location}
        minZoom={4}
        maxZoom={17}
        className="w-full h-full relative"
        zoomControl={false}
        attributionControl={false}
        bounds={latLngBounds}
        ref={mapRef}
        worldCopyJump
      >
        <TileLayer url={HERE_MAPS_TILES_URL} />
        {showRoute && (
          <RouteMap
            donorHospitalMarker={donorHospitalMarker}
            txCenterMarker={txCenterMarker}
            organType={organType}
            locations={locations}
            legs={legs}
            aircrafts={aircrafts}
            aircraftsMarkers={aircraftsMarkers}
          />
        )}
        {!showRoute && <MapMarkers markers={markersList} organType={organType} />}
      </MapContainer>
      {mapRef.current && <MapControls map={mapRef.current} defaultCenter={donorHospitalMarker.location} />}
      {canViewSuggestedRoute && hasOrTime && (
        <SuggestedRoutes
          isOpen={isSuggestedRoutesOpen}
          onOpen={onOpen}
          onClose={handleSuggestionsClose}
          onHighlightRoute={handleHighlightRoute}
        />
      )}
    </>
  );
};
